import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { classNames } from 'primereact/utils';
import { Ripple } from 'primereact/ripple';
import { Badge } from 'primereact/badge';

type AppMenuItemProps = {
    label: string;
    icon: string;
    to?: string;
    url?: string;
    items?: AppMenuItemProps[];
    badge?: string;
    badgeClassName?: string;
    className?: string;
    parentKey?: string;
    target?: string;
    disabled?: boolean;
    visible?: boolean;
    separator?: boolean;
    root?: boolean;
};

type AppMenuProps = {
    model: AppMenuItemProps[];
    onMenuItemClick: (event: { originalEvent: React.MouseEvent<HTMLAnchorElement, MouseEvent>; item: AppMenuItemProps }) => void;
    layoutColorMode: string;
};

const AppMenuitem = (props: AppMenuItemProps & { parentKey?: string; onMenuItemClick: AppMenuProps['onMenuItemClick'] }) => {
    const [activeMenu, setActiveMenu] = useState('');
    const location = useLocation();

    const item = props;
    const key = props.parentKey ? props.parentKey + '-' + props.label : String(props.label);

    const isActiveRoute = item.to && location.pathname === item.to;
    const active = activeMenu === key || isActiveRoute;

    const onMenuItemClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, item: AppMenuItemProps) => {
        if (item.disabled) {
            event.preventDefault();
            return;
        }

        if (item.items) {
            setActiveMenu(active ? '' : key);
        }

        if (props.onMenuItemClick) {
            props.onMenuItemClick({ originalEvent: event, item });
        }
    };

    const itemIcon = classNames('layout-menuitem-icon', item.icon);
    const itemBadgeClassName = classNames('ml-auto', item.badgeClassName);

    const subMenu = item.items && item.visible !== false && (
        <CSSTransition timeout={{ enter: 1000, exit: 450 }} classNames="layout-submenu" in={!!active} key={key}>
            <ul>
                {item.items.map((child, i) => {
                    return <AppMenuitem key={child.label} onMenuItemClick={props.onMenuItemClick} parentKey={key} {...child} />;
                })}
            </ul>
        </CSSTransition>
    );

    return (
        <li className={classNames({ 'layout-root-menuitem': props.root, 'active-menuitem': active })}>
            {props.root && item.visible !== false && <div className="layout-menuitem-root-text">{item.label}</div>}
            {(!item.to || item.items) && item.visible !== false ? (
                <a href={item.url} onClick={(e) => onMenuItemClick(e, item)} className={classNames(item.className, 'p-ripple')} target={item.target} tabIndex={0}>
                    <i className={itemIcon}></i>
                    <span className="layout-menuitem-text">{item.label}</span>
                    {item.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                    {item.badge && <Badge value={item.badge} className={itemBadgeClassName} />}
                    <Ripple />
                </a>
            ) : null}

            {item.to && !item.items && item.visible !== false ? (
                <NavLink to={item.to} onClick={(e) => onMenuItemClick(e, item)} className={classNames(item.className, 'p-ripple')} target={item.target} end>
                    <i className={itemIcon}></i>
                    <span className="layout-menuitem-text">{item.label}</span>
                    {item.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                    {item.badge && <Badge value={item.badge} className={itemBadgeClassName} />}
                    <Ripple />
                </NavLink>
            ) : null}

            {subMenu}
        </li>
    );
};

const AppMenu: React.FC<AppMenuProps> = ({ model, onMenuItemClick, layoutColorMode }) => {
    return (
        <ul className="layout-menu">
            {model.map((item, i) => {
                return !item.separator ? (
                    <AppMenuitem key={`${item.label}-${i}`} onMenuItemClick={onMenuItemClick} {...item} />
                ) : (
                    <li key={`separator-${i}`} className="menu-separator"></li>
                );
            })}
        </ul>
    );
};

export default AppMenu;